// The ONE list-screen table scaffold (UI Layering Sweep Phase 2, List
// archetype §4c): the four-way state render every keyset list screen repeats —
// pending → Skeleton table rows, error → InlineError (in-page notice, never
// toast-only; beside the table when rows are already loaded), empty → the
// caller's EmptyState, loaded → DataTable that pages the keyset. Wires 1:1
// onto `useListQuery`'s result. Everything else (columns, ariaLabel,
// getRowId, onRowClick, virtual, ...) passes through to DataTable untouched.
//
// How the next page is reached is DERIVED from whether the table is
// virtualized (#2217, founder ruling), never a per-caller option:
//   - virtualized → the table owns a scrollport, so it auto-loads as the
//     window approaches the last loaded row (DataTable `onReachEnd`), with no
//     button and no page budget;
//   - not virtualized → document scroll (config-container lists, §4b.3;
//     detail-page sections; a list with a row expanded), so the container
//     `onScroll` never fires and the keyset "Load more" button stays.
//
// Fill mode (§4b.3) is auto-detected from `virtual.fill`: the wrapper then
// renders a flex-column chain (flex: 1 / min-height: 0) instead of Stack
// (display: grid, which breaks the flex-height chain from
// `Page variant="workspace"` and leaves the fill table content-grown).

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";

import {
  getListViewState,
  setListViewState,
  type ListViewState,
} from "../../lib/listViewState";
import type {
  DataTableProps,
  DataTableReachEndEvent,
  DataTableViewportState,
} from "../../ui";
import {
  Button,
  DataTable,
  Inline,
  InlineError,
  Skeleton,
  Stack,
} from "../../ui";

/** Rows in the next-page skeleton that takes the Load-more button's slot on
 *  scroll-paged tables: a visible cue, not a viewport's worth.
 *  ASSUMED(no doc states the footer skeleton height; three 40px rows read
 *  as "more is coming" without pushing the table up.) */
const NEXT_PAGE_SKELETON_ROWS = 3;

export interface PaginatedTableSectionProps<Item> extends Omit<
  DataTableProps<Item>,
  "data" | "emptyMessage" | "onReachEnd"
> {
  // Rendered when the list resolved empty — the caller's <EmptyState .../>
  // (List archetype: one primary CTA, omitted when the caller lacks manage).
  emptyState: ReactNode;
  // Present = the list read failed; renders InlineError in the table region.
  errorMessage?: string;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  isPending: boolean;
  items: Item[];
  /** Cache identity returned by useListQuery for this list's current scope. */
  listStateKey?: string;
  // Load-more button label (non-virtualized tables only). Default "Load more"
  // ("Loading..." in flight).
  loadMoreLabel?: string;
  onLoadMore: () => void;
  // Skeleton accessible label, e.g. "Loading API keys". Default "Loading".
  skeletonLabel?: string;
  skeletonRows?: number;
}

/**
 * The view-state store is keyed by `listStateKey`, and three things have to
 * reset together when that key changes: the store read, the write-back ref,
 * and DataTable's uncontrolled sort/filter/scroll state. Keying the
 * state-owning body — rather than DataTable alone, which left the refs holding
 * the previous scope's viewport and flushed it back under the new key — makes
 * that atomic: React unmounts the outgoing body first (flushing under its own
 * key) and the incoming one reads its own entry in its mount initializers.
 */
export function PaginatedTableSection<Item>(
  props: PaginatedTableSectionProps<Item>,
) {
  return <PaginatedTableSectionBody {...props} key={props.listStateKey} />;
}

function PaginatedTableSectionBody<Item>({
  emptyState,
  errorMessage,
  hasNextPage,
  isFetchingNextPage,
  isPending,
  items,
  listStateKey,
  loadMoreLabel = "Load more",
  onLoadMore,
  skeletonLabel = "Loading",
  skeletonRows = 5,
  ...tableProps
}: PaginatedTableSectionProps<Item>) {
  // In-flight guard: `fetchNextPage` with TanStack's default
  // `cancelRefetch: true` restarts the same page on a second call rather than
  // ignoring it, so reaching the end twice mid-fetch must fire once.
  const loadMoreLocked = useRef(false);
  // Empty-yield breaker: the row count (in DataTable's sorted/filtered model)
  // at which the last auto-load fired. A completed fetch that left it
  // unchanged — every row of the page failed a client-side filter — stops
  // auto-loading until the count actually changes, or the keyset would be
  // walked to exhaustion from a scrollport that never leaves its end.
  const lastReachEndRowCount = useRef<number | null>(null);
  // Lazy initializer: the LRU is touched once per mount — i.e. once per
  // `listStateKey` — not on every render.
  const [restoredViewState] = useState(() =>
    listStateKey === undefined ? undefined : getListViewState(listStateKey),
  );
  // Undefined until the user actually moves the viewport, so a mount that
  // restores and never scrolls leaves the stored entry untouched.
  const latestViewStateRef = useRef<ListViewState | undefined>(undefined);
  const frameRef = useRef<number | null>(null);
  const flushViewState = useCallback(() => {
    if (frameRef.current !== null) {
      window.cancelAnimationFrame(frameRef.current);
      frameRef.current = null;
    }
    if (
      listStateKey !== undefined &&
      latestViewStateRef.current !== undefined
    ) {
      setListViewState(listStateKey, latestViewStateRef.current);
    }
  }, [listStateKey]);

  // Cleanup only: captures a viewport the rAF write had not yet flushed when
  // the route (or the scope) changed out from under this list.
  useLayoutEffect(() => flushViewState, [flushViewState]);

  useEffect(() => {
    if (!isFetchingNextPage) {
      loadMoreLocked.current = false;
    }
  }, [isFetchingNextPage]);

  // Live-region announcements are written straight into the DOM node: the
  // sr-only status is an assistive-tech channel, not render state, and the
  // "Loaded N more rows" text depends on the row count at fetch start.
  const statusRef = useRef<HTMLDivElement>(null);
  const fetchStartCountRef = useRef<number | null>(null);
  const itemCount = items.length;
  useEffect(() => {
    const status = statusRef.current;
    if (isFetchingNextPage) {
      if (fetchStartCountRef.current === null) {
        fetchStartCountRef.current = itemCount;
        if (status) {
          status.textContent = "Loading more rows";
        }
      }
      return;
    }
    const startCount = fetchStartCountRef.current;
    if (startCount === null) {
      return;
    }
    fetchStartCountRef.current = null;
    if (status) {
      const added = Math.max(0, itemCount - startCount);
      status.textContent = `Loaded ${String(added)} more rows, ${String(itemCount)} total`;
    }
  }, [isFetchingNextPage, itemCount]);

  // Single exit: every state resolves into `content` so hooks can be declared
  // above this block without a rules-of-hooks hazard. The four rendered
  // outputs are unchanged.
  const isFillMode = tableProps.virtual?.fill === true;
  // A virtualized table owns its scrollport (UI-LAW §4b.3), so it pages by
  // scroll; everything else document-scrolls and keeps the button.
  const isScrollPaged = tableProps.virtual !== undefined;
  let content: ReactNode;

  if (isPending) {
    // §4b.3 fill mode covers the remaining viewport height; fixed-height
    // sections keep the caller's row count.
    content = (
      <Skeleton
        fill={isFillMode}
        label={skeletonLabel}
        rows={skeletonRows}
        shape="table-row"
      />
    );
  } else if (errorMessage != null && items.length === 0) {
    // A read error with NO loaded rows replaces the table region entirely. A
    // failed "Load more" (TanStack v5 puts the whole infinite query in error
    // state even with pages loaded) must NOT blank rows the user already has:
    // keep the DataTable and render the InlineError beside it, above the
    // Load-more footer.
    content = <InlineError>{errorMessage}</InlineError>;
  } else if (items.length === 0) {
    content = emptyState;
  } else {
    const inlineErrorBesideTable =
      errorMessage != null ? <InlineError>{errorMessage}</InlineError> : null;

    const handleReachEnd = ({ rowCount }: DataTableReachEndEvent) => {
      if (
        !hasNextPage ||
        isFetchingNextPage ||
        loadMoreLocked.current ||
        lastReachEndRowCount.current === rowCount
      ) {
        return;
      }
      loadMoreLocked.current = true;
      lastReachEndRowCount.current = rowCount;
      onLoadMore();
    };

    // A failed next page in scroll mode has no button to click again, and
    // the breaker (rightly) refuses to re-fire at an unchanged row count, so
    // the retry is explicit.
    const retryLoadMore = () => {
      lastReachEndRowCount.current = null;
      loadMoreLocked.current = true;
      onLoadMore();
    };

    const loadMoreFooter = !hasNextPage ? null : isScrollPaged ? (
      isFetchingNextPage ? (
        <Skeleton
          aria-hidden="true"
          className="marker-paginated-table-section__loading"
          rows={NEXT_PAGE_SKELETON_ROWS}
          shape="table-row"
        />
      ) : errorMessage != null ? (
        <Inline>
          <Button
            size="sm"
            type="button"
            variant="secondary"
            onClick={retryLoadMore}
          >
            Retry
          </Button>
        </Inline>
      ) : null
    ) : (
      <Inline>
        <Button
          disabled={isFetchingNextPage}
          size="sm"
          type="button"
          variant="secondary"
          onClick={onLoadMore}
        >
          {isFetchingNextPage ? "Loading..." : loadMoreLabel}
        </Button>
      </Inline>
    );

    // DataTable emits anchor coordinates already resolved against its own
    // sorted/filtered row model, so this is a straight 1:1 store write — no
    // row-height arithmetic and no second row lookup to disagree with it.
    const handleViewportChange = (viewport: DataTableViewportState) => {
      latestViewStateRef.current = {
        anchorIndex: viewport.anchorIndex,
        anchorOffset: viewport.anchorOffset,
        anchorRowId: viewport.anchorRowId,
        columnFilters: viewport.columnFilters,
        sorting: viewport.sorting,
      };
      if (listStateKey === undefined || frameRef.current !== null) {
        return;
      }
      // Coalesce a scroll gesture's events into one store write per frame.
      frameRef.current = window.requestAnimationFrame(() => {
        flushViewState();
      });
    };

    const table = (
      <DataTable
        data={items}
        {...tableProps}
        aria-busy={isFetchingNextPage}
        initialAnchor={restoredViewState}
        initialColumnFilters={restoredViewState?.columnFilters}
        initialSorting={restoredViewState?.sorting}
        onReachEnd={hasNextPage && isScrollPaged ? handleReachEnd : undefined}
        onViewportChange={handleViewportChange}
      />
    );
    const status = isScrollPaged ? (
      <div
        aria-live="polite"
        className="marker-paginated-table-section__status"
        ref={statusRef}
        role="status"
      />
    ) : null;

    // §4b.3 fill mode: flex-column wrapper so the fill table receives the
    // workspace-page height chain and owns the scroll.
    content = isFillMode ? (
      <div className="marker-paginated-table-section marker-paginated-table-section--fill">
        {table}
        {inlineErrorBesideTable}
        {loadMoreFooter}
        {status}
      </div>
    ) : (
      <Stack className="marker-paginated-table-section" gap={3}>
        {table}
        {inlineErrorBesideTable}
        {loadMoreFooter}
        {status}
      </Stack>
    );
  }

  return <>{content}</>;
}
